import { useEffect } from 'react';

import { Helmet } from '@xing-com/crate-helmet';
import { useDeviceInfo } from '@xing-com/crate-xinglet';
import type { GlobalScope } from '@xing-com/crate-xinglet/internal';
import { useRuntimeConfig } from '@xing-com/crate-xinglet/internal';
import { useAppliedTheme, useExperiment } from '@xing-com/hub';

import { DarkColorscheme } from './dark-colorscheme';
import { DarkColorschemeV2 } from './dark-colorscheme-v2';
import { LightColorscheme } from './light-colorscheme';
import { LightColorschemeV2 } from './light-colorscheme-v2';
import { useUsercentricsEvent } from './use-user-centrics-event';
import { useUsercentricsEventV2 } from './use-user-centrics-event-v2';
import { getCookie, setCookie } from './utils';

declare const globalThis: GlobalScope & {
  ucMobileSdk?: unknown;
};

type CookieConsentUsercentricsProps = {
  id?: string;
  disableBanner?: boolean;
};

const defaultId = 'Tr_3B1GDkkB1q2';

const useSkipBanner = (): boolean => {
  const { entryPoint } = useRuntimeConfig();
  const isLoginSite = entryPoint === '@xing-com/crate-login-root';

  const { isWebview } = useDeviceInfo();

  const isBrowserEnvironment = typeof window !== 'undefined';
  if (!isBrowserEnvironment) return true;

  const isSyncedFromWebview = typeof globalThis.ucMobileSdk === 'object';
  if (isWebview && !isSyncedFromWebview) return true;

  const host = window.location.host;
  const enabledByCookie = getCookie('crate-testing-allow-consent') === 'true';
  const isLocalhostOrEnv =
    /[k]?env\.xing\.com|localhost:|^192\.168|http:\/\/application$/.test(host);
  if (isLocalhostOrEnv && !enabledByCookie) return true;

  const urlContainsPercy = window.location.href.includes('percy');
  if (urlContainsPercy) return true;

  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  if (isSafari && isLoginSite) return true;

  return false;
};

export const CookieConsentUsercentrics: React.FC<
  CookieConsentUsercentricsProps
> = ({ id = defaultId, disableBanner }) => {
  // set cookie for old consent if not present to prevent it from opening in legacy apps
  useEffect(() => {
    const COOKIE_NAME = 'userConsent';
    const COOKIE_VALUE = JSON.stringify({
      marketing: false,
      version: '5',
    });
    const COOKIE_EXPIRY_DAYS = 365;
    const existingCookie = getCookie(COOKIE_NAME);

    if (!existingCookie) {
      // Set cookie if not present
      setCookie(COOKIE_NAME, COOKIE_VALUE, COOKIE_EXPIRY_DAYS);
    }
  }, []);

  const appliedTheme = useAppliedTheme();
  const isDarkMode = appliedTheme === 'dark';

  const skipBanner = useSkipBanner();
  const enableUsercentricsV3 = useExperiment('WFS-115') === 'B';

  // Both hooks self-guard based on which API is available
  useUsercentricsEvent();
  useUsercentricsEventV2();

  if (disableBanner || skipBanner) return null;

  // Render appropriate colorscheme based on version
  const ColorschemeComponent = enableUsercentricsV3
    ? isDarkMode
      ? DarkColorscheme
      : LightColorscheme
    : isDarkMode
      ? DarkColorschemeV2
      : LightColorschemeV2;

  if (enableUsercentricsV3) {
    return (
      <>
        <ColorschemeComponent />
        <Helmet>
          <script>
            {`window.UC_UI_DOMAINS = {
              crossDomainConsentSharingIFrame: 'https://www.xing.com/cross-domain-bridge.html'
            };`}
          </script>
          <script src="https://web.cmp.usercentrics.eu/tcf/stub.js" />
          <script
            id="usercentrics-cmp"
            src="https://web.cmp.usercentrics.eu/ui/loader.js"
            async
            data-settings-id={id}
          />
        </Helmet>
      </>
    );
  }

  return (
    <>
      <ColorschemeComponent />
      <Helmet>
        <script>
          {`window.UC_UI_DOMAINS = {
            crossDomainConsentSharingIFrame: 'https://www.xing.com/cross-domain-bridge.html'
          };`}
        </script>
        <script
          id="usercentrics-cmp"
          src="https://app.usercentrics.eu/browser-ui/latest/loader.js"
          async
          data-settings-id={id}
        />
      </Helmet>
    </>
  );
};
